/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useTranslation } from "common/utils/i18n"
import { MavConstant } from "./mavConstants"

export const incidentGroupContentKey = {
  [MavConstant.mavIncidentGroupingAllergy]:
    "incidents.drawer.incidentTypeHeading.foodAllergy",
  [MavConstant.mavIncidentGroupingFoodContamination]:
    "incidents.drawer.incidentTypeHeading.foodContamination",
  [MavConstant.mavIncidentGroupingPoisoning]:
    "incidents.drawer.incidentTypeHeading.foodPoisoning",
  [MavConstant.mavIncidentGroupingUndercooked]:
    "incidents.drawer.incidentTypeHeading.undercookedOvercookedFood",
  [MavConstant.mavIncidentGroupingSpoiledFood]:
    "incidents.drawer.incidentTypeHeading.spoiltFood",
  [MavConstant.mavIncidentGroupingExpiredProduct]:
    "incidents.drawer.incidentTypeHeading.expiredProducts",
  [MavConstant.mavIncidentGroupingSeriousCrime]:
    "incidents.drawer.incidentTypeHeading.crimeByMerchant",
  [MavConstant.mavIncidentGroupingHarassment]:
    "incidents.drawer.incidentTypeHeading.harassmentByMerchant",
  [MavConstant.mavIncidentGroupingAppAndInStorePricingDontMatch]:
    "incidents.drawer.incidentTypeHeading.priceMismatch",
  [MavConstant.mavIncidentGroupingItemIsntOrDoesntWorkAsAdvertised]:
    "incidents.drawer.incidentTypeHeading.itemNotAsDescribed",
  [MavConstant.mavIncidentGroupingSpillageMessyOrDamagedPackaging]:
    "incidents.drawer.incidentTypeHeading.badCondition",
  [MavConstant.mavIncidentGroupingFakeCompletionOrder]:
    "incidents.drawer.incidentTypeHeading.wronglyCompletedOrder",
  [MavConstant.mavIncidentGroupingNotFollowingCustomerRequirements]:
    "incidents.drawer.incidentTypeHeading.requirementsNotFollowed",
  [MavConstant.mavIncidentGroupingSolicitingCustomers]:
    "incidents.drawer.incidentTypeHeading.solicitingCustomers",
  [MavConstant.mavIncidentGroupingColdFood]:
    "incidents.drawer.incidentTypeHeading.coldFood",
  [MavConstant.mavIncidentGroupingSoggyFood]:
    "incidents.drawer.incidentTypeHeading.soggyFood",
  [MavConstant.mavIncidentGroupingFoodNotTasty]:
    "incidents.drawer.incidentTypeHeading.foodNotTasty",
  [MavConstant.mavIncidentGroupingMissingOrWrongItems]:
    "incidents.drawer.incidentTypeHeading.itemIssue",
  [MavConstant.mavIncidentGroupingStoreTooBusyClosingOrClosed]:
    "incidents.drawer.incidentTypeHeading.storeUnavailable",
  [MavConstant.mavIncidentGroupingProductUnavailable]:
    "incidents.drawer.incidentTypeHeading.productsUnavailable",
  [MavConstant.mavIncidentGroupingExceedsTargetPrepTime]:
    "incidents.drawer.incidentTypeHeading.exceededPrepTime",
} as const

export const useIncidentGroupingContent = (incidentGrouping?: number) => {
  const content = useTranslation(
    incidentGroupContentKey[
      incidentGrouping as keyof typeof incidentGroupContentKey
    ],
  )
  if (!content || incidentGrouping === undefined) {
    return "-"
  }
  return content
}

export const incidentAliasContentKey = {
  [MavConstant.mavIAFatalFoodAllergy]:
    "incidents.drawer.incidentTypeBody.fatalCase",
  [MavConstant.mavIAFoodAllergyRequiringMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.seriousCase",
  [MavConstant.mavIAMildFoodAllergyWithoutMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.mildCase",
  [MavConstant.mavIAFatalHazardousContamination]:
    "incidents.drawer.incidentTypeBody.fatalCase",
  [MavConstant.mavIAHazardousContaminationRequiringMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.seriousCase",
  [MavConstant.mavIAHazardousContaminationWithoutMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.seriousCase",
  [MavConstant.mavIAFoodContaminationByANonHazardousObject]:
    "incidents.drawer.incidentTypeBody.mildCase",
  [MavConstant.mavIAFatalFoodPoisoning]:
    "incidents.drawer.incidentTypeBody.fatalCase",
  [MavConstant.mavIAFatalMassFoodPoisoning]:
    "incidents.drawer.incidentTypeBody.multiplePeopleAffected",
  [MavConstant.mavIAMassFoodPoisoningRequiringMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.multiplePeopleAffected",
  [MavConstant.mavIAFoodPoisoningRequiringMedicalAssistance]:
    "incidents.drawer.incidentTypeBody.seriousCase",
  [MavConstant.mavIAUnderOrOvercookedFoodNonAnimalProductAndOther]:
    "incidents.drawer.incidentTypeBody.nonMeatProducts",
  [MavConstant.mavIAReligiousRestrictionsViolated]:
    "incidents.drawer.incidentTypeBody.religiousRestrictionsNotFollowed",
  [MavConstant.mavIADidntFollowCustomerInstructions]:
    "incidents.drawer.incidentTypeBody.instructionsNotFollowed",
  [MavConstant.mavIAMissingItems]:
    "incidents.drawer.incidentTypeBody.missingItems",
  [MavConstant.mavIAWrongItems]: "incidents.drawer.incidentTypeBody.wrongItems",
  [MavConstant.mavIAStoreClosed]:
    "incidents.drawer.incidentTypeBody.storeClosed",
  [MavConstant.mavIAStoreTooBusy]:
    "incidents.drawer.incidentTypeBody.storeBusy",
  [MavConstant.mavIAStoreClosingSoon]:
    "incidents.drawer.incidentTypeBody.storeClosingSoon",
} as const

export const useIncidentAliasContent = (incidentAlias?: number) => {
  const content = useTranslation(
    incidentAliasContentKey[
      incidentAlias as keyof typeof incidentAliasContentKey
    ],
  )
  if (!content || incidentAlias === undefined) {
    return "-"
  }
  return content
}

// exported from this https://gitlab.myteksi.net/mobile/monorepo/-/blob/master/apps/mex/deliveries_modules/module/mav/lib/util/incident_activity_mapping.dart#L10
export const timelineActivityCodeKey = {
  [MavConstant.appeaseTypeNotRefunded]: "incidents.drawer.activity.notRefunded",
  [MavConstant.appeaseTypeRefunded]: "incidents.drawer.activity.refunded",
  [MavConstant.appeaseTypeIssuedVoucher]:
    "incidents.drawer.activity.voucherIssued",
  [MavConstant.appealApproved]: "incidents.drawer.activity.appealSuccess",
  [MavConstant.appealRejected]: "incidents.drawer.activity.appealFailed",
  [MavConstant.appeaseTypeMexIssuedVoucher]:
    "incidents.drawer.activity.voucherIssuedByYou",
  [MavConstant.appeaseTypeMexIssuedVoucherRedeemed]:
    "incidents.drawer.activity.mexIssuedVoucherRedeemed",
  [MavConstant.appeaseTypeMexIssuedVoucherExpired]:
    "incidents.drawer.activity.mexIssuedVoucherExpired",
  [MavConstant.clawbackFinish]: "incidents.drawer.activity.mexClawbackFinished",
} as const

export const useTimelineActivityCodeContent = (activityCode?: number) => {
  const content = useTranslation(
    timelineActivityCodeKey[
      activityCode as keyof typeof timelineActivityCodeKey
    ],
  )
  if (!content || activityCode === undefined) {
    return "-"
  }
  return content
}
